import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';

import { authAPI, getUserErrorMessage } from '../api/api';
import { BRAND_ASSETS } from '../brand/assets';

const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const hasSession = Boolean(localStorage.getItem('token'));

  const [status, setStatus] = useState<'pending' | 'verifying' | 'verified' | 'error'>(
    token ? 'verifying' : 'pending'
  );
  const [message, setMessage] = useState('');
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!token) {
      return;
    }

    let cancelled = false;
    authAPI
      .verifyEmail(token)
      .then(() => {
        if (cancelled) {
          return;
        }
        setStatus('verified');
        if (localStorage.getItem('token')) {
          navigate('/home', { replace: true });
        }
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        setStatus('error');
        setMessage(
          getUserErrorMessage(
            err,
            'This verification link is invalid or has expired. Request a new one after logging in.'
          )
        );
      });

    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  const handleResend = async () => {
    if (!hasSession || resending) {
      return;
    }
    setResending(true);
    setMessage('');
    try {
      const response = await authAPI.resendVerification();
      setMessage(response.message);
    } catch (err: unknown) {
      setMessage(
        getUserErrorMessage(
          err,
          'We could not send another verification email. Please try again in a moment.'
        )
      );
    } finally {
      setResending(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="container"
      style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <div className="card" style={{ maxWidth: '520px', width: '100%', textAlign: 'center' }}>
        <img
          src={BRAND_ASSETS.logo}
          alt="WisemenResearch"
          style={{
            display: 'block',
            width: '160px',
            height: 'auto',
            margin: '0 auto 20px',
          }}
        />
        <h1 style={{ color: 'var(--neon-cyan)', marginBottom: '16px' }}>Verify your email</h1>

        {status === 'verifying' && (
          <>
            <div className="spinner" style={{ margin: '24px auto' }} />
            <p style={{ color: 'var(--text-secondary)' }}>Confirming your email…</p>
          </>
        )}

        {status === 'verified' && (
          <>
            <p style={{ color: 'var(--text-primary)', marginBottom: '24px' }}>
              Your email is verified. You can now use the research library, community, and Ask.
            </p>
            <Link to="/login" className="btn btn-primary">
              Continue to login
            </Link>
          </>
        )}

        {status === 'pending' && (
          <>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: '24px' }}>
              We sent a verification link to your email. Open that link to unlock the research
              library, community, and Ask. Check spam if you do not see it within a few minutes.
            </p>
            {hasSession ? (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleResend}
                disabled={resending}
                style={{ marginBottom: '16px' }}
              >
                {resending ? 'Sending…' : 'Resend verification email'}
              </button>
            ) : (
              <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                Already have an account?{' '}
                <Link to="/login" style={{ color: 'var(--neon-cyan)' }}>
                  Log in
                </Link>{' '}
                to request a new link.
              </p>
            )}
          </>
        )}

        {status === 'error' && (
          <div className="error" role="alert" style={{ marginBottom: '20px' }}>
            {message}
          </div>
        )}

        {message && status !== 'error' && (
          <p style={{ color: 'var(--neon-lime)', marginTop: '12px' }}>{message}</p>
        )}

        {hasSession && (
          <button
            type="button"
            className="btn"
            onClick={handleLogout}
            style={{ marginTop: '20px' }}
          >
            Log out
          </button>
        )}
      </div>
    </motion.div>
  );
};

export default VerifyEmail;
